import React, { useState, useMemo } from 'react';
import { Search, Plus, Trash2, Loader2, Undo2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axiosInstance from '../utils/axiosInstance';
import { groupSearchResults } from './CustomOrderModal';

/**
 * Inline product editor for scenario comparison.
 * Reuses Boxopedia search and manual-entry patterns from CustomOrderModal.
 */
const ScenarioProductEditor = ({ products, onProductsChange, baselineProducts }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [rawSearchResults, setRawSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [manualName, setManualName] = useState('');
  const [manualDims, setManualDims] = useState({ w: '', h: '', l: '', weight: '', qty: '1' });

  const searchResults = useMemo(() => groupSearchResults(rawSearchResults), [rawSearchResults]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const response = await axiosInstance.get('api/packaging-data/', {
        params: { search: searchQuery, page_size: 50, has_dimensions: 'true' },
      });
      setRawSearchResults(response.data.data || []);
    } catch {
      setRawSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const addFromBoxopedia = (group) => {
    const pid = `box-${group.key}`;
    const existingIdx = products.findIndex((p) => p.id === pid);
    if (existingIdx >= 0) {
      onProductsChange(products.map((p, i) => (i === existingIdx ? { ...p, quantity: p.quantity + 1 } : p)));
      return;
    }
    const isMultiBox = group.boxes.length > 1;
    const largestBox = group.display_dims;
    onProductsChange([
      ...products,
      {
        id: pid,
        variant_id: group.shopify_variant_id || pid,
        title: group.product_name,
        variant_title: '',
        sku: group.product_sku || '',
        quantity: 1,
        unit_price: 0,
        cost: 0,
        weight: group.total_weight,
        dimensions: { width: largestBox.width, height: largestBox.height, length: largestBox.length },
        shipped_on_pallet: group.shipped_on_pallet || false,
        image_url: null,
        ...(isMultiBox
          ? {
              packaging_boxes: group.boxes.map((b) => ({
                width: b.width,
                height: b.height,
                length: b.length,
                weight: b.weight || 0,
              })),
            }
          : {}),
      },
    ]);
  };

  const addManualProduct = () => {
    const w = parseFloat(manualDims.w);
    const h = parseFloat(manualDims.h);
    const l = parseFloat(manualDims.l);
    const wt = parseFloat(manualDims.weight);
    const qty = parseInt(manualDims.qty) || 1;
    if (!manualName.trim() || isNaN(w) || isNaN(h) || isNaN(l) || w <= 0 || h <= 0 || l <= 0) return;
    const id = `manual-${Date.now()}`;
    onProductsChange([
      ...products,
      {
        id,
        variant_id: id,
        title: manualName.trim(),
        variant_title: '',
        sku: '',
        quantity: qty,
        unit_price: 0,
        cost: 0,
        weight: isNaN(wt) ? 0 : wt,
        dimensions: { width: w, height: h, length: l },
        image_url: null,
      },
    ]);
    setManualName('');
    setManualDims({ w: '', h: '', l: '', weight: '', qty: '1' });
  };

  // Added products come without a price: let the user type one so the
  // comparison margin can count them.
  const setMoney = (id, field, value) => {
    const num = parseFloat(value);
    onProductsChange(products.map((p) => (p.id === id ? { ...p, [field]: isNaN(num) ? 0 : num } : p)));
  };

  const removeProduct = (id) => onProductsChange(products.filter((p) => p.id !== id));

  const updateQuantity = (id, delta) =>
    onProductsChange(products.map((p) => (p.id === id ? { ...p, quantity: Math.max(1, p.quantity + delta) } : p)));

  const setQuantity = (id, value) => {
    const num = parseInt(value);
    if (!isNaN(num) && num >= 1) {
      onProductsChange(products.map((p) => (p.id === id ? { ...p, quantity: num } : p)));
    }
  };

  // Diff helpers
  const getProductKey = (p) => p.variant_id || p.id;
  const baselineMap = useMemo(() => {
    const m = new Map();
    baselineProducts.forEach((p) => m.set(getProductKey(p), p));
    return m;
  }, [baselineProducts]);

  const getDiffStatus = (product) => {
    const base = baselineMap.get(getProductKey(product));
    if (!base) return 'added';
    if (product.quantity !== base.quantity) return 'modified';
    return 'unchanged';
  };

  // Products in baseline but removed from scenario
  const scenarioKeys = useMemo(() => new Set(products.map(getProductKey)), [products]);
  const removedProducts = useMemo(
    () => baselineProducts.filter((p) => !scenarioKeys.has(getProductKey(p))),
    [baselineProducts, scenarioKeys]
  );

  const restoreProduct = (product) => {
    onProductsChange([...products, { ...product }]);
  };

  const diffBorder = {
    added: 'border-l-4 border-l-emerald-500',
    modified: 'border-l-4 border-l-amber-500',
    unchanged: '',
  };

  return (
    <div className="space-y-4">
      {/* Boxopedia search */}
      <div>
        <label className="text-xs font-semibold text-muted-foreground block mb-1.5">Add from Boxopedia</label>
        <div className="flex gap-2">
          <Input
            placeholder="Search by product name or SKU..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="text-xs"
          />
          <Button variant="outline" size="sm" onClick={handleSearch} disabled={searching || !searchQuery.trim()} className="shrink-0">
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </div>
        {searchResults.length > 0 && (
          <div className="mt-2 rounded-lg border border-border max-h-40 overflow-y-auto">
            {searchResults.map((group) => (
              <div key={group.key} className="flex items-center justify-between px-3 py-1.5 text-xs border-b border-border/50 last:border-0 hover:bg-muted/50">
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">
                    {group.product_name}
                    {group.boxes.length > 1 && (
                      <span className="ml-1.5 inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 text-[10px] font-semibold">
                        {group.boxes.length} boxes
                      </span>
                    )}
                  </p>
                  <p className="text-muted-foreground">
                    {group.product_sku} &mdash;{' '}
                    {group.boxes.length === 1
                      ? `${group.boxes[0].length}×${group.boxes[0].width}×${group.boxes[0].height} cm`
                      : group.boxes.map((b, i) => `${i > 0 ? ' + ' : ''}${b.length}×${b.width}×${b.height}`).join('')}
                    {' — '}{group.total_weight?.toFixed(2) || '?'}kg
                  </p>
                </div>
                <Button size="sm" variant="ghost" className="h-7 px-2 shrink-0 ml-2" onClick={() => addFromBoxopedia(group)}>
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Manual entry */}
      <div>
        <label className="text-xs font-semibold text-muted-foreground block mb-1.5">Or add manually</label>
        <div className="flex gap-1.5 items-end">
          <div className="flex-1">
            <Input
              placeholder="Product name"
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addManualProduct()}
              className="text-xs"
            />
          </div>
          <input type="number" placeholder="W" value={manualDims.w} onChange={(e) => setManualDims((d) => ({ ...d, w: e.target.value }))} className="w-12 rounded-md border border-border bg-background px-1.5 py-2 text-xs text-center" />
          <input type="number" placeholder="H" value={manualDims.h} onChange={(e) => setManualDims((d) => ({ ...d, h: e.target.value }))} className="w-12 rounded-md border border-border bg-background px-1.5 py-2 text-xs text-center" />
          <input type="number" placeholder="L" value={manualDims.l} onChange={(e) => setManualDims((d) => ({ ...d, l: e.target.value }))} className="w-12 rounded-md border border-border bg-background px-1.5 py-2 text-xs text-center" />
          <input type="number" placeholder="kg" value={manualDims.weight} onChange={(e) => setManualDims((d) => ({ ...d, weight: e.target.value }))} className="w-14 rounded-md border border-border bg-background px-1.5 py-2 text-xs text-center" />
          <input type="number" placeholder="Qty" value={manualDims.qty} onChange={(e) => setManualDims((d) => ({ ...d, qty: e.target.value }))} className="w-12 rounded-md border border-border bg-background px-1.5 py-2 text-xs text-center" />
          <Button size="sm" variant="outline" className="h-9 px-2" onClick={addManualProduct} disabled={!manualName.trim() || !manualDims.w || !manualDims.h || !manualDims.l}>
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1">Dimensions in cm (W × H × L), weight in kg</p>
      </div>

      {/* Product table */}
      <div>
        <label className="text-xs font-semibold text-muted-foreground block mb-1.5">
          Scenario Products ({products.reduce((s, p) => s + (p.quantity || 1), 0)} items)
        </label>
        {products.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
            All products removed. Add products above or restore removed ones below.
          </div>
        ) : (
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted/50 border-b border-border">
                  <th className="px-3 py-1.5 text-left font-medium">Product</th>
                  <th className="px-3 py-1.5 text-left font-medium">Dims (cm)</th>
                  <th className="px-3 py-1.5 text-right font-medium">Weight</th>
                  <th className="px-3 py-1.5 text-right font-medium">Price / cost (€)</th>
                  <th className="px-3 py-1.5 text-center font-medium">Qty</th>
                  <th className="px-3 py-1.5 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => {
                  const status = getDiffStatus(p);
                  const baseQty = baselineMap.get(getProductKey(p))?.quantity;
                  const boxes = p.packaging_boxes;
                  return (
                    <tr key={p.id} className={`border-b border-border/50 last:border-0 ${diffBorder[status]}`}>
                      <td className="px-3 py-2">
                        <p className="font-medium">
                          {p.title}
                          {status === 'added' && (
                            <span className="ml-1.5 inline-flex items-center rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 text-[10px] font-semibold">
                              NEW
                            </span>
                          )}
                          {boxes && boxes.length > 1 && (
                            <span className="ml-1.5 inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 text-[10px] font-semibold">
                              {boxes.length} boxes
                            </span>
                          )}
                        </p>
                        {p.sku && <p className="text-muted-foreground">{p.sku}</p>}
                      </td>
                      <td className="px-3 py-2 font-mono">
                        {boxes && boxes.length > 1 ? (
                          <div className="space-y-0.5">
                            {boxes.map((b, i) => (
                              <div key={i} className="text-[10px]">
                                <span className="text-muted-foreground mr-1">#{i + 1}</span>
                                {b.width}×{b.height}×{b.length}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <>{p.dimensions?.width}×{p.dimensions?.height}×{p.dimensions?.length}</>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{(p.weight || 0).toFixed(2)}kg</td>
                      <td className="px-3 py-2 text-right font-mono">
                        {status === 'added' ? (
                          <div className="inline-flex items-center gap-1">
                            <input type="number" min="0" step="0.01" placeholder="price" value={p.unit_price || ''}
                              onChange={(e) => setMoney(p.id, 'unit_price', e.target.value)}
                              className={`w-16 rounded border bg-background px-1 py-0.5 text-xs text-right ${p.unit_price > 0 ? 'border-border' : 'border-amber-400'}`} />
                            <span className="text-muted-foreground">/</span>
                            <input type="number" min="0" step="0.01" placeholder="cost" value={p.cost || ''}
                              onChange={(e) => setMoney(p.id, 'cost', e.target.value)}
                              className={`w-16 rounded border bg-background px-1 py-0.5 text-xs text-right ${p.cost > 0 ? 'border-border' : 'border-amber-400'}`} />
                          </div>
                        ) : (
                          <span className="text-muted-foreground">
                            {Number(p.unit_price || 0).toFixed(2)} / {Number(p.cost || 0).toFixed(2)}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => updateQuantity(p.id, -1)} className="rounded px-1.5 py-0.5 hover:bg-muted text-muted-foreground font-bold" disabled={p.quantity <= 1}>
                            &minus;
                          </button>
                          <input
                            type="number"
                            min="1"
                            value={p.quantity}
                            onChange={(e) => setQuantity(p.id, e.target.value)}
                            className="w-12 text-center font-bold rounded border border-border bg-background px-1 py-0.5 text-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />
                          <button onClick={() => updateQuantity(p.id, 1)} className="rounded px-1.5 py-0.5 hover:bg-muted text-muted-foreground font-bold">
                            +
                          </button>
                          {status === 'modified' && baseQty != null && (
                            <span className="text-[10px] text-amber-600 dark:text-amber-400 ml-1">was {baseQty}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <button onClick={() => removeProduct(p.id)} className="rounded p-1 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Removed products */}
      {removedProducts.length > 0 && (
        <div>
          <label className="text-xs font-semibold text-muted-foreground block mb-1.5">Removed from baseline</label>
          <div className="rounded-lg border border-border/50 overflow-hidden">
            {removedProducts.map((p) => (
              <div key={getProductKey(p)} className="flex items-center justify-between px-3 py-2 text-xs border-b border-border/50 last:border-0 bg-red-50/50 dark:bg-red-950/10">
                <span className="line-through text-muted-foreground">
                  {p.title} {p.sku ? `(${p.sku})` : ''} — qty {p.quantity}
                </span>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-muted-foreground hover:text-foreground" onClick={() => restoreProduct(p)}>
                  <Undo2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScenarioProductEditor;
